import React, { useState, useEffect } from 'react';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import { IoMdAdd } from "react-icons/io";
import { FaPencilAlt, FaTrashAlt } from "react-icons/fa";
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './contacts.css';

function Contact() {
  const { accountId } = useParams();

  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // =========================================================
  // FETCH CONTACTS
  // =========================================================

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);

        const response = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/contacts/${accountId}`
        );

        setContacts(response.data.data || []);
        setError(null);
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          'Failed to fetch contacts. Please try again.';

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (accountId) {
      fetchContacts();
    } else {
      setError('Invalid account ID');
      setLoading(false);
    }
  }, [accountId]);

  // =========================================================
  // DELETE CONTACT
  // =========================================================

  const handleDeleteContact = async (contactId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You want to delete this contact? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `https://hms.backend.hiragroupcloud.com/api/contacts/${contactId}`
        );

        setContacts((prevContacts) =>
          prevContacts.filter(
            (contact) => contact._id !== contactId
          )
        );

        Swal.fire({
          title: 'Deleted!',
          text: 'Contact has been deleted successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete contact. Please try again.',
          icon: 'error',
          confirmButtonColor: '#004aad'
        });
      }
    }
  };

  // =========================================================
  // SAVE
  // =========================================================

  const handleSaveClick = () => {
    Swal.fire({
      title: 'Success!',
      text: 'Provider details saved successfully!',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    });
  };

  if (loading) {
    return <div className="contact-loading">Loading contacts...</div>;
  }

  return (
    <div className="contact-page">

      {/* ERROR */}
      {error && <div className="contact-error">{error}</div>}

      {/* ADD BUTTON */}
      <div className="contact-add-container">
        <Link to={`/account/${accountId}/contacts/add`} style={{ textDecoration: 'none' }}>
          <Button className="contact-add-button">
            <IoMdAdd /> Add
          </Button>
        </Link>
      </div>

      {/* RESPONSIVE TABLE CONTAINER */}
      <div className="contact-table-wrapper">
        <Table className="contact-table">
          <thead>
            <tr>
              <th className="col-primary">Primary</th>
              <th className="col-name">Name</th>
              <th className="col-email">Email</th>
              <th className="col-role">Role</th>
              <th className="col-ebilling">E-Billing</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>

          <tbody>
            {contacts.length > 0 ? (
              contacts.map((contact, index) => (
                <tr key={contact._id || index}>

                  {/* PRIMARY */}
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="contact-checkbox"
                    />
                  </td>

                  {/* NAME */}
                  <td className="contact-cell-text">
                    {`${contact.title ? contact.title + ' ' : ''}${contact.firstName || ''} ${contact.middleInitial ? contact.middleInitial + ' ' : ''}${contact.lastName || ''}`}
                  </td>

                  {/* EMAIL */}
                  <td className="contact-cell-text">
                    {contact.email && (
                      <a
                        href={`https://mail.google.com/mail/?view=cm&fs=1&to=${contact.email}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-email"
                      >
                        {contact.email}
                      </a>
                    )}
                  </td>

                  {/* ROLE */}
                  <td className="contact-cell-text">{contact.role || '-'}</td>

                  {/* E-BILLING */}
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="contact-checkbox"
                      checked={contact.eBilling || false}
                      readOnly
                    />
                  </td>

                  {/* EDIT WITH SPLIT DROPDOWN DELETE */}
                  <td className="text-center action-cell">
                    <Dropdown as={ButtonGroup} className="contact-dropdown">
                      <Link to={`/account/${accountId}/contacts/edit/${contact._id}`} style={{ display: 'inline-flex' }}>
                        <Button className="contact-split-edit-btn">Edit</Button>
                      </Link>

                      <Dropdown.Toggle split className="contact-split-toggle-btn" id={`dropdown-split-${contact._id}`} />

                      <Dropdown.Menu className="contact-dropdown-menu">
                        <Dropdown.Item
                          onClick={() => handleDeleteContact(contact._id)}
                          className="contact-dropdown-delete"
                        >
                          <FaTrashAlt className="delete-icon" /> Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>

                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="contact-no-data">
                  No contacts available.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* SAVE BUTTON */}
      <div className="contact-save-container">
        <Button className="contact-save-button" onClick={handleSaveClick}>
          <FaPencilAlt style={{ marginRight: '4px' }} /> Save
        </Button>
      </div>

    </div>
  );
}

export default Contact;